import { useEffect, useState, type ReactNode } from 'react';
import { Button, Field, HStack, Input, Spinner, Stack, Text } from '@chakra-ui/react';
import type { Bridge, BrowsedService } from '@casa/bridge';
import { casaCloudUrl, CLOUD_PATH, SERVER_PATH, describeServerInfo, deviceName, isServerPage, pairWithServer, readServerInfo, serverLink, signInToServer, type ServerInfo, type ServerLink, type ServerStore } from '../data/casa_server.ts';
import { joinCloudSpace, sayDeviceKey, setUpCloudSecrets, startCloudSpace, type NewKeys } from '../data/cloud_secrets.ts';
import { checkSourceUrl, fetchFromUrl, isUnencryptedUrl, parseOrThrow, requestReload, DashboardError } from '../data/dashboard.ts';
import { availableStores, readStoreSettings, writeStoreSettings, type StoreInfo, type StoreKind, type StoreSettings } from '../data/stores.ts';
import { ChromeError, ChromeNote } from '../ui/chrome_page.tsx';
import { DeviceSignInDialog } from './device_sign_in_dialog.tsx';
import { SettingsGroup, SettingsRow } from './settings_ui.tsx';
import { JoinWaitingDialog } from './join_waiting_dialog.tsx';
import { EmergencyKeyDialog } from './space_key_dialog.tsx';

// "Dashboard Store" (docs/specs/settings.md §4.2): where dashboards are kept. This device by
// default; a Tilez Hub, added by pairing with it; Tilez Cloud beside it, where a device starts a
// space or joins one with its space key, as its address is known (ST-10, cloud-spaces); a web
// address, read-only. The current store is
// chosen here and nowhere else (ST-2); choosing one does not change the dashboard shown (ST-7).

export const STORE_ICONS: Record<StoreKind, string> = { device: 'mobile-screen', casa: 'server', cloud: 'cloud', url: 'globe' };

function messageOf(error: unknown) : string {
    const issues = error instanceof DashboardError ? error.issues.map((issue) => `\n- ${issue}`).join('') : '';

    return `${error instanceof Error ? error.message : String(error)}${issues}`;
}

/** How long a typed address stays unchanged before the server there is asked what it is. */
const TYPING_MS = 600;

type Asked = { status: 'asking' } | { status: 'done'; info: ServerInfo | undefined } | { status: 'failed'; reason: string };

/**
 * What a Tilez Hub says it is and offers (`GET /api/server`, docs/specs/casa-server.md CS-3): the
 * one this device uses, or, while a server is added, the one at the address typed. Undefined while
 * there is nothing to ask.
 */
function useServerInfo(shell: Bridge, server: ServerLink | string | undefined) : Asked | undefined {
    const [asked, setAsked] = useState<Asked>();
    const key = typeof server === 'string' ? server.trim() : server?.kind;

    useEffect(() => {
        if (server === undefined || (typeof server === 'string' && !/^https?:\/\/[^/\s]+/i.test(server.trim()))) {
            setAsked(undefined);
            return;
        }

        let isCurrent = true;
        setAsked({ status: 'asking' });
        const timer = setTimeout(() => {
            readServerInfo(shell, server).then(
                (info) => isCurrent && setAsked({ status: 'done', info }),
                (error: unknown) => isCurrent && setAsked({ status: 'failed', reason: messageOf(error) }),
            );
        }, typeof server === 'string' ? TYPING_MS : 0);

        return () => {
            isCurrent = false;
            clearTimeout(timer);
        };
        // The server by its address or its kind of link, not the object.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shell, key]);

    return asked;
}

/** What a server is, for a row's detail. */
function describeAsked(asked: Asked | undefined) : string | undefined {
    switch (asked?.status) {
        case undefined:
            return undefined;
        case 'asking':
            return 'Asking the server what it is…';
        case 'failed':
            return `No answer: ${asked.reason}`;
        case 'done':
            return asked.info ? describeServerInfo(asked.info) : 'It does not say what it is: a Tilez Hub older than this app';
    }
}

const hasNoBridge = (asked: Asked | undefined) : boolean => asked?.status === 'done' && asked.info !== undefined && !asked.info.offers.includes('bridge');

/** How long to look for Tilez Hubs on the network (ST-9); they answer within a second or two. */
const LOOK_MS = 3_000;

export type Looked = { status: 'looking' } | { status: 'done'; servers: BrowsedService[] } | { status: 'failed'; reason: string };

/**
 * Looks for the Tilez Hubs that announce themselves on the LAN (`_casa._tcp`, settings ST-9), each
 * with the address it gives (`url` in its TXT record). A new `attempt` looks again.
 */
export function useFoundServers(shell: Bridge, attempt = 0) : Looked {
    const [looked, setLooked] = useState<Looked>({ status: 'looking' });

    useEffect(() => {
        let isCurrent = true;
        setLooked({ status: 'looking' });
        shell.net.browse('_casa._tcp', { timeoutMs: LOOK_MS }).then(
            (services) => isCurrent && setLooked({ status: 'done', servers: services.filter((service) => typeof service.txt.url === 'string' && service.txt.url !== '') }),
            (error: unknown) => isCurrent && setLooked({ status: 'failed', reason: messageOf(error) }),
        );

        return () => {
            isCurrent = false;
        };
    }, [shell, attempt]);

    return looked;
}

/**
 * Pairs this device with the Tilez Hub at `url` by a code (empty where it pairs without one), or
 * signs in where it has users; as its Tilez Hub, or as Tilez Cloud (`store`).
 */
export async function pairOrSignIn(shell: Bridge, url: string, way: { code: string } | { user: string; password: string }, store: ServerStore = 'casa') : Promise<void> {
    const info = await shell.device.info();
    const device = deviceName(info);
    if ('user' in way) {
        await signInToServer(shell, url, way.user, way.password, device, store);
    } else {
        await pairWithServer(shell, url, way.code, device, store);
    }
}

/**
 * Adding Tilez Cloud (settings ST-10; cloud-spaces SP-2 to SP-4; cloud-joining JN-2, JN-3), for people
 * who are not technical: one question first. The first device of a household starts its home, and
 * is shown the emergency key; another types the home code from a device of the home, and waits there
 * until that device lets it in. Where Tilez Cloud has accounts, a device may also sign in with one
 * (cloud-accounts AC-13): into one of its homes, or a new one whose keys are made here (AC-15).
 */
function CloudForm({ shell, onPaired }: { shell: Bridge; onPaired: () => void }) : ReactNode {
    const [step, setStep] = useState<'ask' | 'join'>('ask');
    const [homeCode, setHomeCode] = useState('');
    const [busy, setBusy] = useState<'start' | 'join' | 'account'>();
    const [error, setError] = useState<string>();
    const [started, setStarted] = useState<NewKeys>();
    const [hasAccounts, setHasAccounts] = useState(false);
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [isWaiting, setIsWaiting] = useState(false);

    useEffect(() => {
        let isCurrent = true;

        readServerInfo(shell, casaCloudUrl()).then(
            (info) => isCurrent && setHasAccounts(info?.signIn.includes('account') ?? false),
            () => undefined,
        );

        return () => {
            isCurrent = false;
        };
    }, [shell]);

    const run = async (what: 'start' | 'join' | 'account', action: () => Promise<void>) : Promise<void> => {
        setBusy(what);
        setError(undefined);
        try {
            await action();
        } catch (caught) {
            setError(messageOf(caught));
        } finally {
            setBusy(undefined);
        }
    };

    const name = async () : Promise<string> => deviceName(await shell.device.info());

    return (
        <Stack gap="4" p="4">
            {step === 'ask' ? (
                <Stack gap="3">
                    <Text fontWeight="semibold">Is this your first device on Tilez Cloud?</Text>
                    <Button colorPalette="blue" loading={busy === 'start'} onClick={() => void run('start', async () => setStarted(await startCloudSpace(shell, await name())))}>
                        Yes, start here
                    </Button>
                    <Button variant="outline" onClick={() => setStep('join')}>
                        No, I have one already
                    </Button>
                </Stack>
            ) : (
                <Stack gap="3">
                    <Text>On your other device: Settings → Tilez Cloud → Add a device. Type the code it shows:</Text>
                    <HStack align="flex-start">
                        <Input placeholder="XXXX-XXXX" value={homeCode} fontFamily="mono" fontSize="lg" aria-label="Home code" onChange={(event) => setHomeCode(event.target.value)} />
                        <Button colorPalette="blue" disabled={homeCode.trim() === ''} loading={busy === 'join'} onClick={() => void run('join', async () => {
                            await joinCloudSpace(shell, homeCode, await name());
                            setIsWaiting(true);
                        })}>
                            Join
                        </Button>
                    </HStack>
                    <Button variant="plain" size="sm" alignSelf="flex-start" colorPalette="blue" onClick={() => setStep('ask')}>
                        Back
                    </Button>
                </Stack>
            )}
            {hasAccounts ? (
                <Text fontSize="sm" color="fg.muted">
                    Have an account?{' '}
                    <Button variant="plain" size="sm" colorPalette="blue" px="0" loading={busy === 'account'} onClick={() => setIsSigningIn(true)}>
                        Sign in
                    </Button>
                </Text>
            ) : null}
            {error ? <ChromeError text={error} /> : null}
            {isSigningIn ? (
                <DeviceSignInDialog
                    shell={shell}
                    onClose={() => setIsSigningIn(false)}
                    onSignedIn={(isNew) => {
                        setIsSigningIn(false);

                        if (!isNew) {
                            // Into a home with its keys: wait there to be let in (JN-3).
                            void sayDeviceKey(shell).then(() => setIsWaiting(true));

                            return;
                        }

                        // A new home: its keys are made here, as when a home is started (SP-3).
                        void run('account', async () => {
                            const link = await serverLink(shell, 'cloud');

                            if (!link) {
                                throw new Error('Signed in, but this device did not keep its pairing.');
                            }

                            const { joinKey, recoveryKey } = await setUpCloudSecrets(shell, link);

                            setStarted({ joinKey, recoveryKey });
                        });
                    }}
                />
            ) : null}
            {isWaiting ? (
                <JoinWaitingDialog
                    shell={shell}
                    onIn={() => {
                        setIsWaiting(false);
                        onPaired();
                        // The dashboard is read again, with the passwords it uses.
                        requestReload();
                    }}
                    onLater={() => {
                        setIsWaiting(false);
                        onPaired();
                    }}
                />
            ) : null}
            {started ? (
                <EmergencyKeyDialog
                    keys={started}
                    reason={{ kind: 'started' }}
                    onDone={() => {
                        setStarted(undefined);
                        onPaired();
                    }}
                />
            ) : null}
        </Stack>
    );
}

/** A found server's list: a choice fills in the address; typing it stays possible. */
function FoundServers({ shell, chosen, onChoose }: { shell: Bridge; chosen: string; onChoose: (url: string, isOpen: boolean) => void }) : ReactNode {
    const [attempt, setAttempt] = useState(0);
    const looked = useFoundServers(shell, attempt);

    const lookAgain = (
        <Button size="xs" variant="ghost" color="blue.400" onClick={() => setAttempt((count) => count + 1)}>
            Look again
        </Button>
    );

    let rows: ReactNode;
    if (looked.status === 'looking') {
        rows = (
            <HStack gap="2" px="4" py="3" color="fg.muted">
                <Spinner size="sm" />
                <Text>Looking on this network…</Text>
            </HStack>
        );
    } else if (looked.status === 'failed') {
        rows = <SettingsRow title={<Text as="span" color="fg.muted">This device cannot look on the network</Text>} detail={looked.reason} />;
    } else if (looked.servers.length === 0) {
        rows = <SettingsRow title={<Text as="span" color="fg.muted">None found</Text>} detail="A server may not announce itself; type its address below." end={lookAgain} />;
    } else {
        rows = looked.servers.map((server) => (
            <SettingsRow
                key={server.name}
                icon="server"
                title={server.name}
                detail={server.txt.pairing === 'open' ? `${server.txt.url ?? ''} · pairs without a code` : server.txt.url}
                isCurrent={server.txt.url === chosen}
                opens={false}
                onClick={() => onChoose(server.txt.url ?? '', server.txt.pairing === 'open')}
            />
        ));
    }

    return <SettingsGroup title="On this network">{rows}</SettingsGroup>;
}

/**
 * Pairing with a Tilez Hub by its address and a code from it (the server's command line or its
 * admin page); or, where the server has users (its `/api/server` says `password`), signing in.
 */
function PairServerForm({ shell, onPaired }: { shell: Bridge; onPaired: () => void }) : ReactNode {
    const [url, setUrl] = useState('');
    const [code, setCode] = useState('');
    const [user, setUser] = useState('');
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();
    /** The address of a server found on the network that says it pairs without a code (CASA_PAIRING=open). */
    const [openUrl, setOpenUrl] = useState<string>();
    const asked = useServerInfo(shell, url);
    const isOpen = (openUrl !== undefined && openUrl === url) || (asked?.status === 'done' && asked.info?.signIn.includes('open') === true);
    const signsIn = asked?.status === 'done' && asked.info?.signIn.includes('password') === true;

    const pair = async () : Promise<void> => {
        setBusy(true);
        setError(undefined);
        try {
            await pairOrSignIn(shell, url, signsIn ? { user, password } : { code: isOpen ? '' : code });
            onPaired();
        } catch (caught) {
            setError(messageOf(caught));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Stack gap="3" p="4">
            <FoundServers
                shell={shell}
                chosen={url}
                onChoose={(chosen, open) => {
                    setUrl(chosen);
                    setOpenUrl(open ? chosen : undefined);
                }}
            />
            <Field.Root>
                <Field.Label>Server address</Field.Label>
                <Input type="url" placeholder="https://casa.example.com" value={url} aria-label="Server address" onChange={(event) => setUrl(event.target.value)} />
                {asked ? <Field.HelperText data-testid="server-info">{describeAsked(asked)}</Field.HelperText> : null}
            </Field.Root>
            {signsIn ? (
                <>
                    <Field.Root>
                        <Field.Label>Name</Field.Label>
                        <Input value={user} autoComplete="username" aria-label="Name" onChange={(event) => setUser(event.target.value)} />
                    </Field.Root>
                    <Field.Root>
                        <Field.Label>Password</Field.Label>
                        <Input type="password" value={password} autoComplete="current-password" aria-label="Password" onChange={(event) => setPassword(event.target.value)} />
                        <Field.HelperText>Your user on this server. The password is not kept on this device.</Field.HelperText>
                    </Field.Root>
                </>
            ) : isOpen ? (
                <ChromeNote status="info" text="This server pairs without a code." />
            ) : (
                <Field.Root>
                    <Field.Label>Pairing code</Field.Label>
                    <Input placeholder="ABCD-EFGH" value={code} aria-label="Pairing code" onChange={(event) => setCode(event.target.value)} />
                    <Field.HelperText>
                        From the server's command line ("pair"), or its admin page in a browser (its address followed by /admin). Leave it empty for a server that pairs without codes.
                    </Field.HelperText>
                </Field.Root>
            )}
            <Button colorPalette="blue" alignSelf="flex-start" disabled={url.trim() === '' || (signsIn && (user.trim() === '' || password === ''))} loading={busy} onClick={() => void pair()}>
                {signsIn ? 'Sign in' : 'Pair'}
            </Button>
            {error ? <ChromeError text={error} /> : null}
        </Stack>
    );
}

/** A web address that serves a dashboard file, checked before it is kept. */
function WebAddressForm({ shell, onAdded }: { shell: Bridge; onAdded: (address: { url: string; authorization?: string }) => void }) : ReactNode {
    const [url, setUrl] = useState('');
    const [authorization, setAuthorization] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();

    const problem = url.trim() === '' ? undefined : checkSourceUrl(url);
    const address = { url: url.trim(), ...(authorization.trim() !== '' ? { authorization: authorization.trim() } : {}) };

    const add = async () : Promise<void> => {
        setBusy(true);
        setError(undefined);
        try {
            parseOrThrow(await fetchFromUrl(shell, address), `at ${address.url}`);
            onAdded(address);
        } catch (caught) {
            setError(messageOf(caught));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Stack gap="3" p="4">
            <Field.Root invalid={problem !== undefined}>
                <Field.Label>Address</Field.Label>
                <Input type="url" placeholder="https://example.com/casa-dashboard.json" value={url} data-testid="source-url" onChange={(event) => setUrl(event.target.value)} />
                <Field.ErrorText>{problem}</Field.ErrorText>
            </Field.Root>
            <Field.Root>
                <Field.Label>Authorization header</Field.Label>
                <Input type="password" autoComplete="off" value={authorization} data-testid="source-authorization" onChange={(event) => setAuthorization(event.target.value)} />
                <Field.HelperText>If the server asks for one (for example "Bearer …"); kept on this device only.</Field.HelperText>
            </Field.Root>
            {problem === undefined && url.trim() !== '' && isUnencryptedUrl(url) ? (
                <ChromeNote status="warning" text="This address is not encrypted (http://). The dashboard holds passwords and tokens, and anything between this device and the server can read them." />
            ) : null}
            <Button colorPalette="blue" alignSelf="flex-start" disabled={url.trim() === '' || problem !== undefined} loading={busy} onClick={() => void add()}>
                Add
            </Button>
            {error ? <ChromeError text={error} /> : null}
        </Stack>
    );
}

export function StorePart({ shell, onChanged }: { shell: Bridge; onChanged: () => void }) : ReactNode {
    const [settings, setSettings] = useState<StoreSettings>();
    const [stores, setStores] = useState<StoreInfo[]>([]);
    const [adding, setAdding] = useState<'casa' | 'cloud' | 'url' | undefined>();
    const [revision, setRevision] = useState(0);
    const [link, setLink] = useState<ServerLink>();
    const asked = useServerInfo(shell, link);

    useEffect(() => {
        void (async () => {
            setSettings(await readStoreSettings(shell));
            setStores(await availableStores(shell));
            setLink(await serverLink(shell));
        })();
    }, [shell, revision]);

    const update = async (next: StoreSettings) : Promise<void> => {
        await writeStoreSettings(shell, next);
        setAdding(undefined);
        setRevision((value) => value + 1);
        onChanged();
    };

    const remove = async (kind: 'casa' | 'cloud' | 'url') : Promise<void> => {
        if (!settings) {
            return;
        }
        if (kind === 'casa') {
            await shell.storage.remove(SERVER_PATH);
        }
        if (kind === 'cloud') {
            await shell.storage.remove(CLOUD_PATH);
        }

        await update({ current: settings.current === kind ? 'device' : settings.current, url: kind === 'url' ? undefined : settings.url });
    };

    if (!settings) {
        return null;
    }
    if (isServerPage()) {
        return (
            <SettingsGroup
                note={`In a browser, dashboards are kept on the Tilez Hub this page comes from.${hasNoBridge(asked) ? ' This server has no bridge to the home network: the drivers of a dashboard shown here cannot reach devices at home.' : ''}`}
            >
                <SettingsRow icon={STORE_ICONS.casa} title="Tilez Hub" detail={location.host} isCurrent />
                {asked ? <SettingsRow title="What it is" detail={describeAsked(asked)} data-testid="server-info" /> : null}
            </SettingsGroup>
        );
    }

    const has = (kind: StoreKind) : boolean => stores.some((store) => store.kind === kind);

    return (
        <Stack gap="6">
            <SettingsGroup title="Keep dashboards on" note="New dashboards go here, and its dashboards are listed under Dashboards. Choosing another store doesn't change the dashboard shown.">
                {stores.map((store) => (
                    <SettingsRow
                        key={store.kind}
                        icon={STORE_ICONS[store.kind]}
                        title={store.title}
                        detail={store.isWritable ? store.detail : `${store.detail ?? ''} · read-only`}
                        isCurrent={settings.current === store.kind}
                        onClick={() => void update({ ...settings, current: store.kind })}
                        opens={false}
                        data-testid={`store-${store.kind}`}
                    />
                ))}
            </SettingsGroup>
            <SettingsGroup title="Other stores">
                {has('casa') && asked ? <SettingsRow icon={STORE_ICONS.casa} title="The Tilez Hub" detail={describeAsked(asked)} data-testid="server-info" /> : null}
                {has('casa') ? (
                    <SettingsRow icon={STORE_ICONS.casa} title="Forget the Tilez Hub" detail="Its dashboards stay on the server" onClick={() => void remove('casa')} opens={false} color="#f87171" />
                ) : (
                    <SettingsRow icon={STORE_ICONS.casa} title="Add a Tilez Hub" detail="Pair with it, or sign in" onClick={() => setAdding(adding === 'casa' ? undefined : 'casa')} />
                )}
                {adding === 'casa' ? <PairServerForm shell={shell} onPaired={() => void update({ ...settings, current: 'casa' })} /> : null}
                {has('cloud') ? (
                    <SettingsRow icon={STORE_ICONS.cloud} title="Forget Tilez Cloud" detail="Its dashboards stay there" onClick={() => void remove('cloud')} opens={false} color="#f87171" />
                ) : (
                    <SettingsRow icon={STORE_ICONS.cloud} title="Add Tilez Cloud" detail="Keep your dashboards in the cloud, for all your devices" onClick={() => setAdding(adding === 'cloud' ? undefined : 'cloud')} data-testid="add-cloud" />
                )}
                {adding === 'cloud' ? <CloudForm shell={shell} onPaired={() => void update({ ...settings, current: 'cloud' })} /> : null}
                {has('url') ? (
                    <SettingsRow icon={STORE_ICONS.url} title="Remove the web address" onClick={() => void remove('url')} opens={false} color="#f87171" />
                ) : (
                    <SettingsRow icon={STORE_ICONS.url} title="Add a web address" detail="A dashboard file on a web server, read-only" onClick={() => setAdding(adding === 'url' ? undefined : 'url')} />
                )}
                {adding === 'url' ? <WebAddressForm shell={shell} onAdded={(url) => void update({ current: 'url', url })} /> : null}
            </SettingsGroup>
        </Stack>
    );
}
